import { defineStore, storeToRefs } from "pinia"
import { computed, ref, type Ref } from "vue"
import { useInstructionStore } from "@/store/instructions.store"
import { Purchase } from "@/models/purchase.model"
import { useSocketSynchronizationHandlers } from "@/composables/socket/socket-synchronization-handlers"
import { type Instruction } from "@/models/instruction.model"
import { useRelatedObjectsManager } from "@/composables/socket/related-objects-manager"
import type { Sale } from "@/models/sale.model.ts"

export const usePurchaseStore = defineStore("purchaseStore", () => {
  const instructionStore = useInstructionStore()
  const { instructions } = storeToRefs(instructionStore)

  const purchases = computed(() => {
    const purchases = []
    for (const instruction of instructions.value) {
      purchases.push(...instruction.purchases)
    }
    return purchases
  })

  // All purchases that have been retrieved for the purpose of showing outstanding purchases
  const retrievedPurchases: Ref<{ [key: string]: Purchase }> = ref({})

  const { pushToRelatedObjects: pushPurchaseToRelatedObjects, removeFromRelatedObjects } =
    useRelatedObjectsManager(
      instructions,
      (purchase: Purchase) => purchase.instruction,
      (instruction: Instruction) => instruction.id!,
      (instruction: Instruction) => instruction.purchases
    )
  const { pushToRelatedObjects: pushSaleToRelatedObjects } = useRelatedObjectsManager(
    instructions,
    (sale: Sale) => sale.instruction,
    (instruction: Instruction) => instruction.id!,
    (instruction: Instruction) => instruction.sales
  )

  function pushPurchaseAndSaleToRelatedObjects(purchase: Purchase) {
    pushPurchaseToRelatedObjects(purchase)
    if (purchase.associatedSale) {
      // Sometimes the socket received a new purchase before the associated sale. In that case, the sale WILL
      // be created in the ModelManager (by Purchase childModel() call in constructor).
      // However, the subsequent sale creation socket message will not be ignored, because the sale already exists.
      // This also means that the sale will not be added to the instruction.sales array./
      // Therefore, we preemptively add the sale to the related instruction here.
      pushSaleToRelatedObjects(purchase.associatedSale)
    }
  }

  useSocketSynchronizationHandlers(
    Purchase,
    null,
    pushPurchaseAndSaleToRelatedObjects,
    deletePurchaseFromCollections,
    false
  )

  /**
   * Deletes purchase from related Instruction and from retrievedPurchases.
   * @param purchase
   */
  function deletePurchaseFromCollections(purchase: Purchase) {
    removeFromRelatedObjects(purchase)
    // In addition to removing it from the instruction, delete it from the the retrievedPurchases.
    delete retrievedPurchases.value[purchase.id!]
  }

  function addToRetrievedPurchases(purchases: Purchase[]) {
    for (const purchase of purchases) {
      retrievedPurchases.value[purchase.id!] = purchase
    }
  }

  const allPurchases = computed(() => {
    const combined = Object.values(retrievedPurchases.value)
    for (const purchase of purchases.value) {
      if (!retrievedPurchases.value[purchase.id!]) {
        combined.push(purchase)
      }
    }
    return combined
  })

  const outstandingPurchases = computed(() => {
    return allPurchases.value.filter((purchase) => purchase.isOutstanding())
  })

  return {
    purchases,
    pushPurchaseToRelatedObjects,
    removeFromRelatedObjects,
    addToRetrievedPurchases,
    deletePurchaseFromCollections,
    outstandingPurchases,
    allPurchases
  }
})
